/**
 * liabilityReconcileApi.js — Phase 85.1
 *
 * Client for the liability payment reconciliation endpoints. Every call
 * degrades quietly: if the Phase 85.1 migration hasn't been applied on this
 * database the server replies `{ available: false }` and the callers simply
 * render nothing.
 */
import axios from 'axios';
import { supabase } from '@/lib/supabaseClient';

const API = process.env.REACT_APP_BACKEND_URL;

async function authHeaders() {
  let { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    // The session can still be restoring on a cold page load. One short retry
    // avoids a silent 401 that would hide the whole feature.
    await new Promise((r) => setTimeout(r, 700));
    ({ data: { session } } = await supabase.auth.getSession());
  }
  const token = session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const UNAVAILABLE = { available: false, events: [], unmatched_transactions: [] };

async function get(path, { retry = true } = {}) {
  try {
    const res = await axios.get(`${API}${path}`, { headers: await authHeaders(), timeout: 20000 });
    return res.data;
  } catch (e) {
    // On a cold load the Supabase session can still be restoring, and a single
    // 401 would silently hide the whole feature. Retry once before giving up.
    if (retry) {
      await new Promise((r) => setTimeout(r, 900));
      return get(path, { retry: false });
    }
    console.warn('liabilityReconcile GET failed:', path, e?.response?.status || e?.message);
    return UNAVAILABLE;
  }
}

async function post(path, body) {
  const res = await axios.post(`${API}${path}`, body || {}, {
    headers: await authHeaders(), timeout: 20000,
  });
  return res.data;
}

/** Post-import summary. Re-runs the (idempotent) scan for that import. */
export function fetchReconciliationSummary(importId) {
  const q = importId ? `?import_id=${encodeURIComponent(importId)}` : '';
  return get(`/api/liabilities/reconciliation/summary${q}`);
}

/** Payment activity + balance provenance for one liability. */
export function fetchLiabilityPaymentEvents(liabilityId) {
  return get(`/api/liabilities/${encodeURIComponent(liabilityId)}/payment-events`);
}

export function fetchCanonicalBalances() {
  return get('/api/liabilities/canonical');
}

/** Debt-freedom countdown across every liability (Phase 85.3).
 *  `extraMonthly` models "what if I pay this much more each month". */
export function fetchFreedomTimeline(extraMonthly) {
  const q = extraMonthly > 0 ? `?extra_monthly=${encodeURIComponent(extraMonthly)}` : '';
  return get(`/api/liabilities/freedom-timeline${q}`);
}

/** Close a loan (paid off) or retire it into its refinanced replacement. */
export function closeLiability(liabilityId, { reason, replacementLiabilityId, closedAt } = {}) {
  return post(`/api/liabilities/${encodeURIComponent(liabilityId)}/close`, {
    reason: reason || 'paid_off',
    replacement_liability_id: replacementLiabilityId || undefined,
    closed_at: closedAt || undefined,
  });
}

/** Confirm a suggested match. Pass `liabilityId` to relink to another loan. */
export function confirmPaymentEvent(eventId, liabilityId) {
  return post(`/api/liabilities/payment-events/${encodeURIComponent(eventId)}/confirm`,
    liabilityId ? { liability_id: liabilityId } : {});
}

export function rejectPaymentEvent(eventId) {
  return post(`/api/liabilities/payment-events/${encodeURIComponent(eventId)}/reject`);
}

export function reversePaymentEvent(eventId) {
  return post(`/api/liabilities/payment-events/${encodeURIComponent(eventId)}/reverse`);
}

/** Link a transaction we couldn't match to a liability the user picked. */
export function linkTransactionToLiability(transactionId, liabilityId) {
  return post('/api/liabilities/reconciliation/link', {
    transaction_id: transactionId, liability_id: liabilityId,
  });
}

/** Record an authoritative balance the user (or their lender) reported. */
export function reportLiabilityBalance(liabilityId, outstandingAmount, asOfDate) {
  return post(`/api/liabilities/${encodeURIComponent(liabilityId)}/balance-snapshots`, {
    outstanding_amount: outstandingAmount,
    as_of_date: asOfDate || undefined,
    source: 'user_reported',
    confidence: 'high',
  });
}

/** Human copy for the match-status chips. */
export const MATCH_STATUS_LABEL = {
  suggested: 'Needs confirmation',
  auto_linked: 'Linked automatically',
  user_confirmed: 'Confirmed by you',
  rejected: 'Not this loan',
  reversed: 'Reversed',
};

/** Human copy for allocation provenance. */
export const ALLOCATION_SOURCE_LABEL = {
  lender_reported: 'Split reported by lender',
  statement_reported: 'Split reported on statement',
  amortization_estimate: 'Split estimated from loan terms',
  user_entered: 'Split entered by you',
  unknown: 'Split not known yet',
};
